import { FC } from 'react';
import cls from './report-progress-bar.module.scss';
import { useCachedSelectedProject, useTranslation } from 'hooks';
import { ReportProgressState } from 'shared/hooks/useReportProgress';
import { ProgressBarSegment } from 'shared/ui/progress-bar-segment/progress-bar-segment';
import Button from 'shared/ui/button/button';
import { useHistoryModeToggle } from 'shared/hooks/useHistoryModeToggle';
import { PaidStatus } from 'store/api/apiTypes';

interface ReportProgressBarProps {
  progress: ReportProgressState;
}

const getSegmentProgress = (
  index: number,
  currentStageIndex: number,
  allComplete: boolean,
  stages: ReportProgressState['stages']
) => {
  if (allComplete || index < currentStageIndex) {
    return 1;
  }

  if (index > currentStageIndex) {
    return 0;
  }

  const stage = stages[index];
  const completedSubsteps = stage.substeps.filter(Boolean).length;
  return completedSubsteps / stage.substeps.length;
};

export const ReportProgressBar: FC<ReportProgressBarProps> = ({ progress }) => {
  const { t } = useTranslation();
  const { stages, currentStageIndex, totalStages, allComplete } = progress;
  const currentStage = stages[currentStageIndex];
  const { selectedProject, showHistory } = useCachedSelectedProject();
  const { handleToggleHistoryMode } = useHistoryModeToggle();

  return (
    <div className={cls.reportProgress}>
      <div className={cls.progressSection}>
        <p className={cls.title}>{t('generatingYourReport')}</p>
        <div className={cls.segments}>
          {stages.map((stage, index) => {
            const segmentProgress = getSegmentProgress(
              index,
              currentStageIndex,
              allComplete,
              stages
            );
            const isActiveSegment =
              index === currentStageIndex && !stage.completed && !allComplete;

            return (
              <ProgressBarSegment
                key={stage.key}
                progress={segmentProgress}
                isActive={isActiveSegment}
              />
            );
          })}
        </div>
        <p className={cls.stageLabel}>
          <span className={cls.stageLabelContent}>
            <span>
              {t('stageOf', undefined, undefined, {
                current: String(currentStageIndex + 1),
                total: String(totalStages),
              })}
            </span>
            <span className={cls.separator}>•</span>
            <span>{t(currentStage.key)}</span>
          </span>
        </p>
      </div>
      {selectedProject?.paid === PaidStatus.PAID && showHistory && (
        <Button theme='text' tone='default' onClick={handleToggleHistoryMode}>
          {t('reportHistory')}
        </Button>
      )}
    </div>
  );
};
